/**
 * @plt/engine — the rules that both the server and the customer page have to
 * agree on. Handover rev7 §6 (quantity), §8 (calendar and lead time) and
 * §9 (payment and invoicing).
 *
 * Stage 020_bootstrap creates the package. What goes in it:
 *   022_calendar_capacity  business days, `bd_add`, lead time, capacity
 *                          (built in SQL; `supabase/migrations/…_calendar_capacity.sql`)
 *   023_engine             pieces per guest, the savoury/sweet balance,
 *                          gluten-free split, production-unit rounding, the
 *                          category money of §5.11
 *   024_catalogue          the published-dish view the engine reads
 *   027_payments_invoicing money, VAT and the invoice lines
 *
 * Nothing here invents a catalogue field, an allergen, a price or an
 * operational rule. Values come from the parameter register (§12) and the
 * seed spreadsheets in docs/, and every one of them is passed in.
 */

/** Handover rev7 §10, *Stack and build* — DECIDED. Every date, business-day
 *  count and cut-off in this system is read in this zone, never the server's. */
export const TIME_ZONE = 'Europe/Budapest'

/** The one language release one ships (§5.12). English exists in the page's
 *  strings; `orders.lang` is `hu` only in release one (§19.3). */
export const RELEASE_ONE_LANGUAGES = ['hu'] as const

export type ReleaseOneLanguage = (typeof RELEASE_ONE_LANGUAGES)[number]

/**
 * §5.3 and §6.7 step 6, as Barna ruled on 10 October 2026 (fix list item 2):
 * **allergen 1 is the single source of truth.** A dish is gluten-free
 * eligible when allergen 1 is explicitly false; the name no longer counts.
 * A blank allergen 1 is an unknown and does not qualify — the same rule as
 * `public.dish_is_gluten_free()`, which the page reads.
 */
export function isGlutenFreeEligible(dish: {
  nameHu?: string | null
  nameEn?: string | null
  allergenGluten?: boolean | null
}): boolean {
  return dish.allergenGluten === false
}

/**
 * §5.11 *Quantities*: "*db* for pieces, *adag* for salads, vegetable sticks
 * and fruit." The catalogue has no unit column, so the unit is read off the
 * category and the name — the vegetable-stick category, or a name that says
 * salad. Fruit is set where fruit lines are built, because fruit is not a
 * dish on either side (§5.6).
 *
 * Named once here, so the server that writes `order_lines.unit` and the test
 * fixtures that build a dish cannot drift apart. It decides a word on a page,
 * never a quantity.
 */
export function dishUnit(dish: {
  category?: string | null
  nameHu?: string | null
  nameEn?: string | null
}): string {
  const isVegetableSticks = (dish.category ?? '') === 'zöldség'
  const saysSalad = /salát|salad/i.test(`${dish.nameHu ?? ''} ${dish.nameEn ?? ''}`)
  return isVegetableSticks || saysSalad ? 'adag' : 'db'
}

export * from './rational.ts'
export * from './types.ts'
export * from './quantity.ts'
export * from './money.ts'
/* Run 19 §16: coffee, priced out of the register rather than from a
   catalogue row. */
export * from './coffee.ts'
export * from './order.ts'
/* The adapters that turn one reading of public.menu_context() or
   public.submission_context() into the shapes above. Shared so that the page
   and the server read one reading the same way (§19.2 principle 2). */
export * from './context.ts'
/* Run 16 item 2: the one formatter every delivery and pickup time is written
   through, so the date and the weekday travel with it everywhere. */
export * from './eventTime.ts'
/* Specification §3.7 (DECIDED 20261023): the delivery radius and the zone
   bands, as the database applies them. */
export * from './delivery.ts'
